/**
 * Reading an "active / inactive" column whose values nobody has told us in advance.
 *
 * Source systems spell this a dozen ways — Active/Inactive, A/I, Y/N, true/false, 1/0,
 * Open/Closed — and the export may use something none of those. So rather than demand a
 * fixed vocabulary, this recognizes the common spellings and lets an administrator name the
 * exceptions (`inactiveValues` / `activeValues` on the feed settings, comma-separated).
 *
 * THE DEFAULT IS ACTIVE, in three separate ways, and that is the important decision here.
 * A blank cell, an unrecognized value, and a feed with no status column mapped at all are all
 * treated as active. Guessing "inactive" would make a client vanish from the picker with no
 * visible cause, and someone would conclude the client is not in the system and open a
 * duplicate — much worse than showing one client too many. The importer counts how many values
 * it could not recognize and reports it, so a mapping that is quietly matching nothing shows up
 * as "0 inactive out of 8,000" rather than passing silently.
 *
 * ADMIN LISTS WIN over the built-in vocabularies, in both directions. An export where "Y"
 * means *inactive* is a real possibility, and "y" is in the built-in active list, so the
 * override has to be able to reverse a built-in rather than merely extend it.
 *
 * Faithful port of functions/src/feeds/clientStatus.js — functions/ deploys standalone and
 * cannot import from web/. This copy backs the admin page's preview of how the export's actual
 * values will be read; if the two drift, that preview lies about what the importer will do.
 */

/** Compared after stripping everything but letters and digits, so "In-Active" matches. */
export const BUILT_IN_INACTIVE = [
  'inactive',
  'notactive',
  'i',
  'n',
  'no',
  'false',
  'f',
  '0',
  'closed',
  'terminated',
  'archived',
  'disabled',
  'deleted',
  'former',
  'expired',
];

export const BUILT_IN_ACTIVE = [
  'active',
  'a',
  'y',
  'yes',
  'true',
  't',
  '1',
  'open',
  'current',
  'live',
];

export function normalizeToken(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/** Parses a comma-separated admin list into normalized tokens, ignoring blanks. */
export function parseValueList(raw) {
  if (Array.isArray(raw)) return raw.map(normalizeToken).filter(Boolean);
  return String(raw || '')
    .split(',')
    .map(normalizeToken)
    .filter(Boolean);
}

/**
 * @returns {{active: boolean, recognized: boolean}} `recognized` is false when the value was
 *   present but matched nothing — the caller counts these so a mis-mapped column is visible.
 *   A blank value is `{active: true, recognized: true}`: absence of a status is not an
 *   unrecognized status, and there is nothing for an administrator to fix about it.
 */
export function readClientStatus(rawValue, settings) {
  const token = normalizeToken(rawValue);
  if (!token) return { active: true, recognized: true };

  const adminInactive = parseValueList(settings && settings.inactiveValues);
  const adminActive = parseValueList(settings && settings.activeValues);
  if (adminInactive.includes(token)) return { active: false, recognized: true };
  if (adminActive.includes(token)) return { active: true, recognized: true };

  if (BUILT_IN_INACTIVE.includes(token)) return { active: false, recognized: true };
  if (BUILT_IN_ACTIVE.includes(token)) return { active: true, recognized: true };

  return { active: true, recognized: false };
}
